import { Box, Flex } from '@chakra-ui/react';
import {
  Button,
  FormFeedback,
  Input,
  OutlineCard,
  Switch,
  useToast
} from '@morressier/alembic';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateJournalSettings } from 'src/api/mutations';
import { FieldLabel } from 'src/components/Field';

interface ReviewerSearchFormData {
  lexiconClassId: string;
  lexiconProjectId: string;
}

const getEmptyFormValues = (): ReviewerSearchFormData => ({
  lexiconClassId: '',
  lexiconProjectId: ''
});

const LexiconClassIdField = ({
  register,
  setValue,
  watch,
  errors,
  clearErrors
}: FormFieldProps) => {
  const watchedValue = watch('lexiconClassId');
  const [tempValue, setTempValue] = React.useState<string>(watchedValue || '');

  useEffect(() => {
    setTempValue(watchedValue || '');
  }, [watchedValue]);

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <FieldLabel
        label="Lexicon Class Id"
        subText=""
        isRequired={true}
        helpText=""
      />
      <Flex
        flexDirection="column"
        width={{ base: '100%', md: '50%' }}
        alignItems="flex-start"
        gap={2}
      >
        <Input
          id="lexiconClassId"
          value={tempValue}
          {...register('lexiconClassId', {
            required: 'Lexicon class Id is required'
          })}
          isInvalid={!!errors.lexiconClassId}
          placeholder="Enter lexicon class id"
          onChange={e => {
            setTempValue(e.target.value);
            if (errors.lexiconClassId) clearErrors('lexiconClassId');
          }}
          onBlur={() => {
            setValue('lexiconClassId', tempValue.trim());
          }}
        />
        {errors.lexiconClassId && (
          <FormFeedback variant="error">
            {errors.lexiconClassId.message}
          </FormFeedback>
        )}
      </Flex>
    </Flex>
  );
};

const LexiconProjectIdField = ({
  register,
  setValue,
  watch,
  errors,
  clearErrors
}: FormFieldProps) => {
  const watchedValue = watch('lexiconProjectId');
  const [tempValue, setTempValue] = React.useState<string>(watchedValue || '');

  useEffect(() => {
    setTempValue(watchedValue || '');
  }, [watchedValue]);

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <FieldLabel
        label="Lexicon Project Id"
        subText=""
        isRequired={true}
        helpText=""
      />
      <Flex
        flexDirection="column"
        width={{ base: '100%', md: '50%' }}
        alignItems="flex-start"
        gap={2}
      >
        <Input
          id="lexiconProjectId"
          value={tempValue}
          {...register('lexiconProjectId', {
            required: 'Lexicon project Id is required'
          })}
          isInvalid={!!errors.lexiconProjectId}
          placeholder="Enter lexicon project id"
          onChange={e => {
            setTempValue(e.target.value);
            if (errors.lexiconProjectId) clearErrors('lexiconProjectId');
          }}
          onBlur={() => {
            setValue('lexiconProjectId', tempValue.trim());
          }}
        />
        {errors.lexiconProjectId && (
          <FormFeedback variant="error">
            {errors.lexiconProjectId.message}
          </FormFeedback>
        )}
      </Flex>
    </Flex>
  );
};

interface FormFieldProps {
  register: any;
  setValue: any;
  watch: any;
  errors: any;
  clearErrors: any;
}

interface ReviewerSearchConfigureProps {
  journalId: string;
  reviewerSearch?: boolean;
  createAccount?: boolean;
  lexiconClassId?: string;
  lexiconProjectId?: string;
  globalReviewerSearch?: boolean;
}

const ReviewerSearchConfigure: React.FC<ReviewerSearchConfigureProps> = ({
  journalId,
  reviewerSearch,
  createAccount,
  lexiconClassId: configLexiconClassId,
  lexiconProjectId: configLexiconProjectId,
  globalReviewerSearch
}) => {
  const toast = useToast();

  // Independent switches
  const [reviewerSearchEnabled, setReviewerSearchEnabled] = useState(
    !!reviewerSearch
  );
  const [createAccountEnabled, setCreateAccountEnabled] = useState(
    !!createAccount
  );
   const [globalReviewerSearchEnabled, setGlobalReviewerSearchEnabled] = useState(
    !!globalReviewerSearch
  );

  const updateSettingMutation = useUpdateJournalSettings(journalId);

  // Separate loading state so auto-save toggles don't affect the "Save Changes" button
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const methods = useForm<ReviewerSearchFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: getEmptyFormValues()
  });

  const {
    handleSubmit,
    formState,
    register,
    setValue,
    watch,
    clearErrors,
    reset
  } = methods;
  const { errors } = formState;

  // Initialize local state from props on mount / journal change.
  // Only keyed on journalId — mutations we trigger will invalidate the
  // query cache and cause prop updates, but we must not overwrite local
  // state in response to those.
  useEffect(() => {
    setReviewerSearchEnabled(!!reviewerSearch);
    setCreateAccountEnabled(!!createAccount);
    setGlobalReviewerSearchEnabled(!!globalReviewerSearch);
    reset({
      lexiconClassId: configLexiconClassId ?? '',
      lexiconProjectId: configLexiconProjectId ?? ''
    });
  }, [journalId]);

  const handleSave = async (formValues: ReviewerSearchFormData) => {
    const configPayload = {
      reviewerSearch: reviewerSearchEnabled,
      lexiconClassId: reviewerSearchEnabled ? formValues.lexiconClassId : '',
      lexiconProjectId: reviewerSearchEnabled ? formValues.lexiconProjectId : '',
      globalLexiconConfig: reviewerSearchEnabled
        ? globalReviewerSearchEnabled
        : false
    };

    setIsSaveLoading(true);
    const res = await updateSettingMutation.mutateAsync({
      data: {},
      config: configPayload
    } as any);
    setIsSaveLoading(false);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Reviewer search enabled',
        isClosable: true
      });
      return;
    }

    toast({
      variant: 'error',
      title:
        typeof res?.message === 'string' ? res.message : 'Something went wrong',
      isClosable: true
    });
  };

  const handleToggleCreateAccount = async (checked: boolean) => {
    setCreateAccountEnabled(checked);
    const res = await updateSettingMutation.mutateAsync({
      data: {},
      config: {
        cms: {
          lexicon: {
            reviewerActionCard: {
              createAccount: checked
            }
          }
        }
      }
    } as any);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: checked
          ? 'Mandatory Reviewer account enabled'
          : 'Mandatory Reviewer account disabled',
        isClosable: true
      });
      return;
    }

    // Revert on failure
    setCreateAccountEnabled(!checked);
    toast({
      variant: 'error',
      title:
        typeof res?.message === 'string' ? res.message : 'Something went wrong',
      isClosable: true
    });
  };

  // Auto-save when reviewer search is disabled — no need to click "Save Changes"
  const handleToggleReviewerSearch = async (checked: boolean) => {
    setReviewerSearchEnabled(checked);

    if (!checked) {
      // Disabling: auto-save immediately, preserving any existing lexicon ids
      // instead of resetting them to empty strings. Global search is disabled
      // alongside reviewer search.
      const lexiconClassId = configLexiconClassId ?? '';
      const lexiconProjectId = configLexiconProjectId ?? '';

      setGlobalReviewerSearchEnabled(false);

      reset({
        lexiconClassId,
        lexiconProjectId
      });

      const res = await updateSettingMutation.mutateAsync({
        data: {},
        config: {
          reviewerSearch: false,
          lexiconClassId,
          lexiconProjectId,
          globalLexiconConfig: false
        }
      } as any);

      if (res.isSuccess) {
        toast({
          variant: 'success',
          title: 'Reviewer search disabled',
          isClosable: true
        });
        return;
      }

      // Revert on failure
      setReviewerSearchEnabled(!!reviewerSearch);
      toast({
        variant: 'error',
        title:
          typeof res?.message === 'string'
            ? res.message
            : 'Something went wrong',
        isClosable: true
      });
    }
  };

  // Global search is saved together with the other fields via "Save Changes"
  const handleToggleGlobalReviewerSearch = (checked: boolean) => {
    setGlobalReviewerSearchEnabled(checked);
  };

  const handleCancel = () => {
    setReviewerSearchEnabled(!!reviewerSearch);
    setCreateAccountEnabled(!!createAccount);
    setGlobalReviewerSearchEnabled(!!globalReviewerSearch);
    reset({
      lexiconClassId: configLexiconClassId ?? '',
      lexiconProjectId: configLexiconProjectId ?? ''
    });
  };

  return (
    <Flex flexDirection="column" gap={6}>
      <OutlineCard borderless paddingX={4} paddingY={6}>
        <Box
          as="form"
          onSubmit={handleSubmit(handleSave)}
          display="grid"
          gap={4}
        >
          {/* Switch 1: Enable Reviewer Search */}
          <Flex
            alignItems={{ base: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            gap={4}
          >
            <FieldLabel
              label="Enable Reviewer Search"
              subText="Allow reviewers to be searched and invited for submissions"
              isRequired={false}
            />
            {/* configure save path : config.reviewerSearch */}
            <Switch
              isChecked={reviewerSearchEnabled}
              onChange={event => {
                handleToggleReviewerSearch(event.target.checked);
              }}
            />
          </Flex>

          {reviewerSearchEnabled && (
            <>
              {/* configure save path : config.lexiconProjectId */}
              <LexiconProjectIdField
                register={register}
                setValue={setValue}
                watch={watch}
                errors={errors}
                clearErrors={clearErrors}
              />
              {/* configure save path : config.lexiconClassId */}
              <LexiconClassIdField
                register={register}
                setValue={setValue}
                watch={watch}
                errors={errors}
                clearErrors={clearErrors}
              />


            <Flex alignItems="left" justifyContent="flex-start" gap={3} pt={2}>
               {/* configure save path : config.globalLexiconConfig */}
            <Switch
              isChecked={globalReviewerSearchEnabled}
              onChange={event => {
                handleToggleGlobalReviewerSearch(event.target.checked);
              }}
              mt={2}
            />
            <FieldLabel
              label="Enable Global Search"
              subText="Allow reviewers to be searched across all journals"
              isRequired={false}
            />
           
          </Flex>
            </>
          )}

          {reviewerSearchEnabled && (
            <Flex gap={2} justifyContent="flex-end" mt={2}>
              <Button
                variant="secondary"
                onClick={handleCancel}
                isDisabled={isSaveLoading}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={isSaveLoading}>
                Save Changes
              </Button>
            </Flex>
          )}
        </Box>
      </OutlineCard>
      <OutlineCard borderless paddingX={4} paddingY={6}>
        <Flex
          alignItems={{ base: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          gap={4}
        >
          <FieldLabel
            label="Mandatory Reviewer account"
            subText="A reviewer account is required before an invitation can be sent."
            isRequired={false}
          />
          {/* configure save path : config.cms.lexicon.reviewerActionCard.createAccount */}
          <Switch
            isChecked={createAccountEnabled}
            onChange={event => {
              handleToggleCreateAccount(event.target.checked);
            }}
          />
        </Flex>
      </OutlineCard>
    </Flex>
  );
};

export default ReviewerSearchConfigure;
